import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import translation from '../translations/translations.json';
import { Languages } from '../../utils/commonUtil';

const fallbackLanguage = navigator.language.startsWith('da') ? Languages.da : Languages.en;

// Our own key, deliberately not the detector's default `i18nextLng`: qampo-ui-library
// bundles a second i18next instance that initializes on import and writes its own
// resolved language — always "en", since it reads index.html's static lang — into that
// shared key. Whichever module loads first wins, and the library usually does, so the
// app read "en" back as if the user had chosen it. See the comment on `detection` below.
export const LANGUAGE_STORAGE_KEY = 'rehabplanner.language';

// The key qampo-ui-library's own i18next instance reads on import.
const LIBRARY_LANGUAGE_STORAGE_KEY = 'i18nextLng';

// index.html ships a static lang="en", but the detector may resolve Danish and the
// user can switch at runtime, so the attribute has to follow i18next. Reads the resolved
// language rather than the requested one, which need not be a language we actually
// speak: `?language=da-DK` resolves to `da`, `?language=de` to the fallback. Guarded
// because this module is also imported from node-environment unit tests, with no DOM.
const syncActiveLanguage = (): void => {
  if (typeof document === 'undefined') return;
  const activeLanguage = i18n.resolvedLanguage ?? i18n.language;
  document.documentElement.lang = activeLanguage;
  // qampo-ui-library's bundled i18next reads this key once, while it is being imported,
  // and every locale-formatted value it renders afterwards follows the language it picked
  // then — it exposes no way to change it later. Our own cache lives elsewhere (see
  // LANGUAGE_STORAGE_KEY), so without this write the library would never learn what the
  // app settled on. main.tsx imports this module first so the write beats that read; a
  // language chosen later in the session (?language=) still reaches the library only on
  // the next load. Storage can be unavailable (private mode, blocked cookies), and the
  // language the library formats by is not worth failing the switch over.
  try {
    localStorage.setItem(LIBRARY_LANGUAGE_STORAGE_KEY, activeLanguage);
  } catch {
    // Nothing to recover: the library falls back to reading the browser's own language.
  }
};

// Registered before init on purpose: `languageChanged` also fires for the initially
// detected language, which is the only way to catch it without duplicating the
// detector's resolution logic here.
i18n.on('languageChanged', syncActiveLanguage);

// Initializing i18n with React
i18n
  .use(LanguageDetector)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // Setting up the resources for translations
    resources: translation,
    supportedLngs: [Languages.en, Languages.da],
    // Two departures from the detector's defaults, both aimed at the same bug — a Danish
    // browser booting the app in English:
    //  - `htmlTag` is dropped from the order. The detector does not stop at the first
    //    detector that answers; it concatenates every candidate and hands the list to
    //    i18next, which takes the first *exactly* supported code. index.html's static
    //    lang="en" therefore beat the browser: "da-DK" is not in supportedLngs, so "en"
    //    matched first and the pass that would have reduced "da-DK" to "da" never ran.
    //  - the cached language gets its own key, away from qampo-ui-library's instance.
    // The languageChanged listener above keeps the html attribute in step either way.
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'sessionStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      lookupSessionStorage: LANGUAGE_STORAGE_KEY,
      lookupCookie: LANGUAGE_STORAGE_KEY
    },
    fallbackLng: fallbackLanguage,
    // Interpolation allows dynamic insertion of values into translated strings.
    interpolation: {
      // By setting `escapeValue` to false, we instruct i18n not to remove special characters or HTML tags.
      escapeValue: false
    },
    // allow keys to have ":" , "."
    nsSeparator: false,
    keySeparator: false
  });

export default i18n;
